import React, { useContext, useEffect, useState } from 'react'
import delay from 'delay'
import { Paper, Snackbar, useTheme, Collapse } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import DoDisturbIcon from '@mui/icons-material/DoDisturb'
import ChecklistIcon from '@mui/icons-material/Checklist'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import hipersendService from '../../services/hipersend'
import { ApiDataWrapperContext } from '../../contexts/ApiDataWrapper'
import Text from '../Text'
import CircularProgressCustom from '../CircularProgressCustom'
import metaWhite from '../../assets/meta-white-color.png'
import metaPrimary from '../../assets/meta-primary-color.png'
import { APP_VERSION } from '../../services/appVersion'
import DeviceDisabled from './DeviceDisabled'
import DeviceSuspended from './DeviceSuspended'
import DeviceConnected from './DeviceConnected'
import DeviceDisconnected from './DeviceDisconnected'
import MetaUpgradePromoBanner from './MetaUpgradePromoBanner'
import PreviewWelcomeHeader from './PreviewWelcomeHeader'

const API_VERSIONS_NEED_QRCODE = ['hiperapi-v1', 'hiperapi-v1-next', 'hiperapi-v2']

export default function DeviceConnection() {
  const theme = useTheme()
  const { getCredentials, provider, fetchData, user, phone, verifiedName, aplicarSuspensao } = useContext(ApiDataWrapperContext)
  const { api: apiId } = getCredentials()
  const metaUpgradePromo = user?.metaUpgradePromo || null
  const upgradeContact = user?.upgradeContact || null
  const showPromo = !!metaUpgradePromo?.isActive && provider !== 'hiperapi-v3-cloud-official'
  const isPreviewTheme = !!theme.isPreview
  const [isInitialLoading, setIsInitialLoading] = useState(true)
  const [isAuthenticated, setIsAuthencated] = useState(false)
  const [lastInstanceStartDate, setLastInstanceStartDate] = useState(null)
  const [isSuspended, setIsSuspended] = useState(false)
  const [isDisabled, setisDisabled] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [qrCodeSuccess, setQrCodeSuccess] = useState(false)

  const getInformations = async () => {
    try {
      const { api, secret } = getCredentials()
      const response = await hipersendService.notificacoesInteligentes.getInformations(api, secret)
      setIsSuspended(response?.isSuspended || false)
      setisDisabled(response?.isDisabled || false)
      setLastInstanceStartDate(response?.lastStart || null)
      if(response?.statusSession === 'isLogged') {
        setIsAuthencated(true)
      } else {
        setIsAuthencated(false)
      }
      // A mesma resposta já traz a suspensão do WhatsApp oficial: ela vai para
      // o contexto, de onde a faixa do layout lê — recarga que reativa (ou
      // suspensão nova) aparece no próximo polling, sem recarregar a página.
      aplicarSuspensao(response)
    } catch (error) {
      console.error(error)
    }
  }

  const renderDeviceStatus = () => {
    if (isSuspended) {
      return <DeviceSuspended />;
    }

    if (isDisabled) {
      return (
        <DeviceDisabled onDisconnect={handleOnDisconnect} />
      );
    }

    if (isAuthenticated) {
      return (
        <DeviceConnected onDisconnect={handleOnDisconnect} lastInstanceStartDate={lastInstanceStartDate}  />
      );
    }
    return (
      <DeviceDisconnected
        onQrCodeGenerated={() => setQrCodeSuccess(true)}
      />
    );
  };

  const handleGetInformationsInitial = async () => {
    await delay(800)
    getInformations()
    setIsInitialLoading(false)
  }

  const handleOnDisconnect = async () => {
    setIsAuthencated(false)
    setSnackbarMessage('Desconectado com sucesso')
    await delay(1000)
    fetchData()
  }

  // só faz polling quando a aba está visível (economiza tráfego pelo proxy)
  useEffect(() => {
    let interval = document.hidden ? null : setInterval(getInformations, 5000)

    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (interval) {
          clearInterval(interval)
          interval = null
        }
      } else {
        getInformations()
        interval = setInterval(getInformations, 5000)
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      if (interval) clearInterval(interval)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  useEffect(() => {
    handleGetInformationsInitial()
  }, [])

  return (
    <div>
      <Snackbar
        open={!!snackbarMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        autoHideDuration={6000}
        onClose={() => setSnackbarMessage('')}
        message={snackbarMessage}
      />
      {isPreviewTheme ? (
        <PreviewWelcomeHeader
          provider={provider}
          isAuthenticated={isAuthenticated}
          phone={phone}
          verifiedName={verifiedName}
        />
      ) : (
        <div className="mb-8 flex space-x-2">
          <Text><AutoAwesomeIcon style={{ fontSize: 35 }} /></Text>
          <div>
            <Text className="text-xl md:text-2xl font-light">Olá, bem vindo!</Text>
            {
              isInitialLoading &&
              <Text>Carregando suas informações</Text>
            }
            {
              !isInitialLoading && !isAuthenticated && !qrCodeSuccess &&
              <Text>Gerando dados para integração, aguarde</Text>
            }
            {
              !isInitialLoading && !isAuthenticated && qrCodeSuccess &&
              <Text>Siga as intruções de integração abaixo</Text>
            }
          </div>
        </div>
      )}
      <Collapse in={!isInitialLoading && (showPromo || provider !== 'hiperapi-v3-cloud-official')}>
        {showPromo ? (
          <MetaUpgradePromoBanner
            promo={metaUpgradePromo}
            upgradeContact={upgradeContact}
            provider={provider}
            apiId={apiId}
            forceVariant="soft"
          />
        ) : provider !== 'hiperapi-v3-cloud-official' ? (
          <div className="mb-8 border-l-4 pl-4" style={{ borderColor: theme.palette.primary.main }}>
            <div className="flex items-center space-x-2">
              <Text className="font-black text-lg">Novidades v{APP_VERSION}</Text>
              <img src={theme.isDark ? metaWhite : metaPrimary} className="w-[80px]" alt="Meta" />
            </div>
            <Text>Whatsapp API oficial agora disponível.</Text>
            <Text>Converse com nossos especialistas para integração.</Text>
          </div>
        ) : null}
      </Collapse>
      {
        !isInitialLoading
        ?
        <div>
          {renderDeviceStatus()}
        </div>
        :
        <div className="h-[640px] flex justify-center items-center">
          <div className="flex flex-col justify-center items-center">
            <CircularProgressCustom />
            <Text className="mt-4 font-bold text-sm">Carregando instância</Text>
          </div>
        </div>
      }
      {
        !isInitialLoading
          && showPromo
          && API_VERSIONS_NEED_QRCODE.includes(provider)
          && metaUpgradePromo?.variant
          && metaUpgradePromo.variant !== 'soft' &&
        <div className="mt-12">
          <MetaUpgradePromoBanner
            promo={metaUpgradePromo}
            upgradeContact={upgradeContact}
            provider={provider}
            apiId={apiId}
          />
        </div>
      }
      {
        API_VERSIONS_NEED_QRCODE.includes(provider) &&
        <div className="mt-12">
          <Paper elevation={5}>
            <Paper elevation={1}>
              <div className="w-full">
                <div className="p-4 md:p-12">
                  <div>
                    <div className="flex items-center space-x-2">
                      <ChecklistIcon style={{ fontSize: 40 }} />
                      <Text className="text-xl">
                        Dicas de utilização e termos de uso
                      </Text>
                    </div>
                    <div className="mt-8">
                      <Text>
                        Faça a leitura do QRCode com seu Telefone/WhatsApp, para realizar os envios através do mesmo. Atente-se a ler as dicas mencionadas abaixo para um correto funcionamento do sistema e também para prevenir bloqueios do seu WhatsApp.
                      </Text>
                    </div>
                  </div>
                  <div className="mt-12">
                    <Text className="text-xl">
                      Como não usar a api
                    </Text>
                    <ul>
                      <li>
                        <div className="flex space-x-2">
                          <DoDisturbIcon />
                          <Text>Não utilizar números que já tenham sido banidos</Text>
                        </div>
                      </li>
                      <li>
                        <div className="flex space-x-2">
                          <DoDisturbIcon />
                          <Text>Não clique em "Sair de todas as sessões" no celular</Text>
                        </div>
                      </li>
                      <li>
                        <div className="flex space-x-2">
                          <DoDisturbIcon />
                          <Text>Não ative um número e já faça envios em massa. Considere usar números ativos a mais de 2 meses no WhatsApp</Text>
                        </div>
                      </li>
                      <li>
                        <div className="flex space-x-2">
                          <DoDisturbIcon />
                          <Text>Seu número pode ser banido se muitos clientes denunciarem como SPAM</Text>
                        </div>
                      </li>
                    </ul>
                  </div>
                  <div className="mt-12">
                    <Text className="text-xl">
                      Dicas de uso para sua API
                    </Text>
                    <ul>
                      <li>
                        <div className="flex space-x-2">
                          <CheckIcon />
                          <Text>Conecte-se ao WhatsApp Web antes de conectar a API</Text>
                        </div>
                      </li>
                      <li>
                        <div className="flex space-x-2">
                          <CheckIcon />
                          <Text>Recomendamos números ativos há mais de 2 meses no WhatsApp</Text>
                        </div>
                      </li>
                      <li>
                        <div className="flex space-x-2">
                          <CheckIcon />
                          <Text>Use versões oficiais do WhatsApp (WhatsApp ou WhatsApp Business)</Text>
                        </div>
                      </li>
                      <li>
                        <div className="flex space-x-2">
                          <CheckIcon />
                          <Text>Entre em grupos do WhatsApp (Grupos do seu provedor e/uo grupo de suporte da sua empresa)</Text>
                        </div>
                      </li>
                      <li>
                        <div className="flex space-x-2">
                          <CheckIcon />
                          <Text>Troque mensagens com pelo menos 5 contatos e estabeleça um diálogo breve com os mesmos</Text>
                        </div>
                      </li>
                      <li>
                        <div className="flex space-x-2">
                          <CheckIcon />
                          <Text>Poste status no WhatsApp, aqui não se preocupe com as visualizações nesses status, o intuito é apenas movimentar sua conta</Text>
                        </div>
                      </li>
                      <li>
                        <div className="flex space-x-2">
                          <CheckIcon />
                          <Text>Após ler o QR Code, aguarde a sincronização completar e mostrar autenticado</Text>
                        </div>
                      </li>
                      <li>
                        <div className="flex space-x-2">
                          <CheckIcon />
                          <Text>Não use WhatsApp clonado por apps de terceiros (SuperClone, Parallel, etc)</Text>
                        </div>
                      </li>
                      <li>
                        <div className="flex space-x-2">
                          <CheckIcon />
                          <Text>Mantenha seu WhatsApp atualizado! Versões antigas podem causar problemas na API</Text>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </Paper>
          </Paper>
        </div>
      }
    </div>
  )
}
